import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import "./AddCircular.css";

const API_URL = "https://aadyaacademy.org/api";

const AddCircular = ({ closeForm, circular }) => {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState("");
  const [fromdate, setFromdate] = useState("");
  const [todate, setTodate] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  const [selectedClasses, setSelectedClasses] = useState([]); // IDs
  const [selectedSections, setSelectedSections] = useState([]); // IDs

  const [loading, setLoading] = useState(true);

  // ✅ Load dropdown data
  useEffect(() => {
    const fetchCategories = axios.get(`${API_URL}/circular-categories`);
    const fetchClasses = axios.get(`${API_URL}/classes`);
    const fetchSections = axios.get(`${API_URL}/sections`);

    Promise.all([fetchCategories, fetchClasses, fetchSections])
      .then(([catRes, classRes, secRes]) => {
        setCategories(catRes.data);
        setClasses(classRes.data.classes || []);
        setSections(secRes.data.sections || []);
      })
      .catch(() => toast.error("❌ Failed to load dropdown data."))
      .finally(() => setLoading(false));
  }, []);

  // ✅ Prefill when editing
  useEffect(() => {
    if (circular?.id) {
      const fetchCircularDetails = async () => {
        try {
          const { data } = await axios.get(`${API_URL}/circulars/${circular.id}`);
          setTitle(data.title || "");
          setDetails(data.description || "");
          setCategory(data.category || "");
          setFromdate(
            data.fromdate ? new Date(data.fromdate).toISOString().split("T")[0] : ""
          );
          setTodate(
            data.todate ? new Date(data.todate).toISOString().split("T")[0] : ""
          );
          setPdfUrl(data.pdf_file_path || null);

          // ✅ Use IDs to pre-select multi-selects
          if (data.class_id) setSelectedClasses([String(data.class_id)]);
          if (data.section_id) setSelectedSections([String(data.section_id)]);
        } catch {
          toast.error("❌ Failed to fetch circular details.");
        }
      };
      fetchCircularDetails();
    }
  }, [circular]);

  const handleClassChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    const values = options.map((o) => o.value);
    setSelectedClasses(values);
  };

  const handleSectionChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    const values = options.map((o) => o.value);
    setSelectedSections(values);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClasses.length) return toast.error("Please select at least one class");
    if (!selectedSections.length) return toast.error("Please select at least one section");

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", details);
      formData.append("category", category);
      formData.append("fromdate", fromdate);
      formData.append("todate", todate);

      selectedClasses.forEach((id) => formData.append("class_ids[]", id));
      selectedSections.forEach((id) => formData.append("section_ids[]", id));

      if (attachment) {
        formData.append("attachment", attachment);
      } else if (pdfUrl) {
        formData.append("pdf_file_path", pdfUrl);
      }

      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (circular?.id) {
        await axios.put(`${API_URL}/circulars/${circular.id}`, formData, config);
      } else {
        await axios.post(`${API_URL}/circulars`, formData, config);
      }

      toast.success("✅ Circular saved successfully!", { autoClose: 3000 });
      setTimeout(closeForm, 1000);
    } catch (error) {
      console.error("Circular API Error:", error.response?.data || error.message);
      if (error.response?.status === 413) {
        toast.error("❌ File is too large. Please upload a smaller PDF.", { autoClose: 5000 });
      } else {
        toast.error(
          error.response?.data?.error || "❌ Failed to save circular.",
          { autoClose: 4000 }
        );
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="add-circular-container">
      <div className="add-circular-content">
        <h2>{circular ? "Edit Circular" : "Add New Circular"}</h2>
        <form onSubmit={handleFormSubmit}>
          <div className="form-group-Title">
            <h4>Circular Title</h4>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group-Details">
            <h4>Circular Details</h4>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              required
            />
          </div>

          <div className="form-group-Category">
            <h4>Category</h4>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group-multi-select">
            <div className="form-group-Class">
              <h4>Select Classes (Multiple)</h4>
              <select
                multiple
                value={selectedClasses}
                onChange={handleClassChange}
                required
              >
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group-Section">
              <h4>Select Sections (Multiple)</h4>
              <select
                multiple
                value={selectedSections}
                onChange={handleSectionChange}
                required
              >
                {sections.map((sec) => (
                  <option key={sec.id} value={sec.id}>
                    {sec.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group-Dates">
            <div>
              <h4>From Date</h4>
              <input
                type="date"
                value={fromdate}
                onChange={(e) => setFromdate(e.target.value)}
                required
              />
            </div>
            <div>
              <h4>To Date</h4>
              <input
                type="date"
                value={todate}
                onChange={(e) => setTodate(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group-File">
            <h4>Upload PDF File</h4>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setAttachment(e.target.files[0])}
            />
            {attachment && <p>Uploaded File: {attachment.name}</p>}
            {pdfUrl && !attachment && (
              <p>
                Existing PDF:&nbsp;
                <a
                  href={`${API_URL}/${pdfUrl.replace(
                    "https://aadyaacademy.org/",
                    ""
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Circular PDF
                </a>
              </p>
            )}
          </div>

          <div className="form-buttons">
            <button className="form-buttons-submit" type="submit">
              Submit
            </button>
            <button
              className="form-buttons-cancel"
              type="button"
              onClick={closeForm}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" pauseOnHover autoClose={3000} />
    </div>
  );
};

export default AddCircular;
